import React from 'react'
import _ from 'lodash'
import {
  Alert,
  Col,
  FormInput,
  FormInputCheckbox,
  FormInputDate,
  FormInputSelect,
  FormInputTextArea,
  Row,
  FormInputText
} from 'camino-react'

import {
  findChargeableDuration,
  minutesToHours,
  minutesToHumanFormat,
  timesheetDuration
} from './TimesheetUtilities'

const timesheetDescriptionReplacement = (duration) => {
  const { hours, minutes } = minutesToHours(_.round(duration * 60, 2))
  const arr = []

  if (_.toNumber(hours)) { arr.push(`${hours} hours`) }
  if (_.toNumber(minutes)) { arr.push(`${minutes} minutes`) }
  if (_.every([hours, minutes], i => _.toNumber(i) === 0)) { arr.push(`${minutes} minutes`) }

  return `Time for ${_.join(arr, ', ')} by`
}

const billingDetailsInfo = (timesheet) => {
  if (_.isNil(timesheet.billing_details)) { return 'No rounding' }

  const { billing_details: { type, minimum_time: minimumTime, round_by: roundBy } } = timesheet
  if (type === 'WorkTypeBillingDetails::Round') {
    if (minimumTime === roundBy) {
      return `Round up to ${minimumTime} minutes`
    }

    return `If under ${minimumTime} minutes, then round to ${minimumTime}, else round to ${roundBy} minutes`
  }

  if (type === 'WorkTypeBillingDetails::Boundary') {
    return `Don't charge until ${minimumTime}, then round to ${roundBy}`
  }

  if (type === 'WorkTypeBillingDetails::Fixed') {
    return `Fixed time of ${minimumTime} minutes`
  }
}

const toSelectOptions = values => _.map(values, ({ id, name }) => ({ value: id, label: name, key: id }))

const TicketInvoicingTimesheet = ({ index, timesheet, serviceGroups, workTypes, workRoles, changeTimesheetField }) => {
  const serviceGroupOptions = toSelectOptions(serviceGroups)
  const chosenServiceGroupOption = _.find(serviceGroupOptions, ['value', timesheet.service_group_id])
  const chosenServiceGroup = _.defaultTo(_.find(serviceGroups, ['id', timesheet.service_group_id]), {})

  const workTypeOptions = toSelectOptions(workTypes)
  const chosenWorkTypeOption = _.find(workTypeOptions, ['value', timesheet.work_type_id])

  const workRoleOptions = toSelectOptions(workRoles)
  const chosenWorkRoleOption = _.find(workRoleOptions, ['value', timesheet.work_role_id])

  const duration = timesheetDuration(timesheet)
  const chargeableDuration = findChargeableDuration(duration, timesheet.billing_details)

  const notInContract = timesheet.chargeable && chosenServiceGroup.managed_service && (
    !_.includes(_.map(_.get(chosenServiceGroup, 'work_types', []), 'id'), timesheet.work_type_id) ||
    !_.includes(_.map(_.get(chosenServiceGroup, 'work_roles', []), 'id'), timesheet.work_role_id)
  )

  return (
    <>
      {notInContract && (
        <Alert type="error">
          That work item is not in that contract, are you sure you want to still make this chargeable?
        </Alert>
      )}

      <Row>
        <Col width={6}>
          <FormInputCheckbox
            id={`timesheet${index}_chargeable`}
            name={`timesheet${index}[chargeable]`}
            value={timesheet.chargeable}
            onValue={checked => changeTimesheetField('chargeable', checked, index)}
          >
            Chargeable?
          </FormInputCheckbox>
          {timesheet.chargeable && (
            <>
              <p><strong>Estimates</strong></p>
              <ul className="list list--no-list-style">
                <label htmlFor="timesheet_chargeable_rate">
                  Chargeable rate (per hr)
                  <FormInputText
                    type="number"
                    id={`timesheet${index}_chargeable_rate`}
                    step=".01"
                    min="0.00"
                    max="100000.00"
                    name={`timesheet${index}[chargeable_rate]`}
                    onValue={value => changeTimesheetField('chargeable_rate', value, index)}
                    value={timesheet.chargeable_rate}
                  />
                </label>
                <li>
                  Charge:
                  {' '}
                  {_.round(timesheet.chargeable_rate * chargeableDuration, 2)}
                </li>
                <li>
                  Chargeable duration:
                  {' '}
                  {minutesToHumanFormat(_.round(chargeableDuration * 60, 2))}
                </li>
                <li>
                  Actual duration:
                  {' '}
                  {minutesToHumanFormat(_.round(duration * 60, 2))}
                </li>
                <li>
                  Using billing details:
                  {' '}
                  {billingDetailsInfo(timesheet)}
                </li>
              </ul>
            </>
          )}
        </Col>
        <Col width={4}>
          <Row>
            <FormInput label="Service group">
              <FormInputSelect
                options={serviceGroupOptions}
                value={chosenServiceGroupOption}
                onValue={value => changeTimesheetField('service_group_id', value, index)}
                disabled={!timesheet.chargeable}
              />
            </FormInput>
          </Row>
          <Row>
            <FormInput label="Work type">
              <FormInputSelect
                options={workTypeOptions}
                value={chosenWorkTypeOption}
                onValue={value => changeTimesheetField('work_type_id', value, index)}
                disabled={!timesheet.chargeable}
              />
            </FormInput>
          </Row>
          <Row>
            <FormInput label="Work role">
              <FormInputSelect
                options={workRoleOptions}
                value={chosenWorkRoleOption}
                onValue={value => changeTimesheetField('work_role_id', value, index)}
                disabled={!timesheet.chargeable}
              />
            </FormInput>
          </Row>
          <Row>
            <FormInput label="Start time">
              <FormInputDate
                enableTime
                disabled={!timesheet.chargeable}
                onValue={date => changeTimesheetField('start_time', date, index)}
                value={timesheet.start_time}
              />
            </FormInput>
          </Row>
          <Row>
            <FormInput label="End time">
              <FormInputDate
                enableTime
                onValue={date => changeTimesheetField('end_time', date, index)}
                disabled={!timesheet.chargeable}
                value={timesheet.end_time}
              />
            </FormInput>
          </Row>
        </Col>
        <Col width={14}>
          <label htmlFor="timesheet_invoice_description">
            Invoice description
            {timesheet.chargeable ? (
              <FormInputTextArea
                id="timesheet_invoice_description"
                rows="6"
                name="timesheet[invoice_description]"
                value={
                  _.replace(
                    timesheet.description,
                    /.*?\b(Time for .*by)\b/g,
                    timesheetDescriptionReplacement(chargeableDuration)
                  )
                }
                onValue={text => changeTimesheetField('description', text, index)}
              />
            ) : (
              <div>
                {timesheet.description}
              </div>
            ) }
          </label>
        </Col>
      </Row>
      <hr />
    </>
  )
}

export default TicketInvoicingTimesheet
